import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';

export function initializeMonaco(): void {
  if (!(self as any).MonacoEnvironment) {
    (self as any).MonacoEnvironment = {
      getWorker(_workerId: string, label: string) {
        const base = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.55.1/esm/vs';
        if (label === 'json') {
          return new Worker(`${base}/language/json/json.worker.js`, { type: 'module' });
        }
        if (label === 'css' || label === 'scss' || label === 'less') {
          return new Worker(`${base}/language/css/css.worker.js`, { type: 'module' });
        }
        if (label === 'html' || label === 'handlebars' || label === 'razor') {
          return new Worker(`${base}/language/html/html.worker.js`, { type: 'module' });
        }
        if (label === 'typescript' || label === 'javascript') {
          return new Worker(`${base}/language/typescript/ts.worker.js`, { type: 'module' });
        }
        return new Worker(`${base}/editor/editor.worker.js`, { type: 'module' });
      }
    };
  }
  registerLanguages();
  defineMonacoThemes();
}

const jsonLanguage: monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  brackets: [
    { open: '{', close: '}', token: 'delimiter.curly' },
    { open: '[', close: ']', token: 'delimiter.square' },
  ],
  tokenizer: {
    root: [
      { include: '@whitespace' },
      { include: '@comments' },
      // property key: a quoted string followed (after optional ws) by ':'
      [/"(?:[^"\\]|\\.)*"(?=\s*:)/, 'string.key'],
      // any other string is a value
      [/"/, { token: 'string.value.quote', bracket: '@open', next: '@stringValue' }],
      [/[{}[\]]/, '@brackets'],
      [/,/, 'delimiter.comma'],
      [/:/, 'delimiter.colon'],
      { include: '@numbers' },
      [/\b(?:true|false)\b/, 'keyword.boolean'],
      [/\bnull\b/, 'keyword.null'],
    ],
    stringValue: [
      [/[^\\"]+/, 'string.value'],
      [/\\./, 'string.escape'],
      [/"/, { token: 'string.value.quote', bracket: '@close', next: '@pop' }],
    ],
    whitespace: [
      [/[ \t\r\n]+/, 'white'],
    ],
    comments: [
      [/\/\/[^\r\n]*/, 'comment'],
      [/\/\*/, 'comment', '@comment'],
    ],
    comment: [
      [/[^/*]+/, 'comment'],
      [/\*\//, 'comment', '@pop'],
      [/[/*]/, 'comment'],
    ],
    numbers: [
      [/-?0[xX][0-9a-fA-F]+/, 'number.hex'],
      [/-?\d+\.\d+([eE][+-]?\d+)?/, 'number.float'],
      [/-?0[bB][01]+/, 'number.binary'],
      [/-?\d+/, 'number'],
    ],
  },
};

const xmlLanguage: monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  tokenizer: {
    root: [
      [/<!--/, 'comment', '@comment'],
      [/<!\[CDATA\[/, 'string.cdata', '@cdata'],
      [/<!/, 'metatag', '@doctype'],
      [/<\?/, 'metatag', '@processingInstruction'],
      { regex: /(<\/)([\w:.-]+)/, action: [{ token: 'delimiter.angle' }, { token: 'tag', next: '@closeTag' }] },
      { regex: /(<)([\w:.-]+)/, action: [{ token: 'delimiter.angle' }, { token: 'tag', next: '@tagAttributes' }] },
      [/&[#\w]+;/, 'constant.entity'],
      [/[^<&]+/, 'text.content'],
    ],
    closeTag: [
      [/[ \t\r\n]+/, 'white'],
      [/>/, 'delimiter.angle', '@pop'],
    ],
    tagAttributes: [
      [/[ \t\r\n]+/, 'white'],
      [/\/?>/, 'delimiter.angle', '@pop'],
      [/([\w:.-]+)(\s*)(=)/, ['attribute.name', 'white', 'delimiter.equals']],
      [/"/, 'attribute.value', '@attrDouble'],
      [/'/, 'attribute.value', '@attrSingle'],
      [/[\w:.-]+/, 'attribute.name'],
    ],
    attrDouble: [
      [/[^"\\]+/, 'attribute.value'],
      [/\\./, 'string.escape'],
      [/"/, 'attribute.value', '@pop'],
    ],
    attrSingle: [
      [/[^'\\]+/, 'attribute.value'],
      [/\\./, 'string.escape'],
      [/'/, 'attribute.value', '@pop'],
    ],
    comment: [
      [/[^-]+/, 'comment'],
      [/-->/, 'comment', '@pop'],
      [/-/, 'comment'],
    ],
    cdata: [
      [/[^\]]+/, 'string.cdata'],
      [/]]>/, 'string.cdata', '@pop'],
      [/]/, 'string.cdata'],
    ],
    doctype: [
      [/[^>]+/, 'metatag'],
      [/>/, 'metatag', '@pop'],
    ],
    processingInstruction: [
      [/[ \t]+/, 'white'],
      [/=/, 'delimiter.equals'],
      [/"[^"]*"|'[^']*'/, 'attribute.value'],
      [/\?>/, 'metatag', '@pop'],
      [/[\w:.-]+/, 'attribute.name'],
      [/[^?\s]+/, 'metatag'],
      [/\?/, 'metatag'],
    ],
  },
};

const markdownLanguage: monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  tokenizer: {
    root: [
      [/^(#{1,6})\s+[^\r\n]*/, 'keyword'],
      [/^(-|\*|\+)\s+/, 'keyword'],
      [/^(\d+\.)\s+/, 'keyword'],
      [/^>\s+/, 'comment'],
      [/^(```)\w*$/, 'string', '@codeblock'],
      [/``[^`]+``/, 'string'],
      [/ `[^`]+`/, 'string'],
      [/\*\*\*[^*\s].*?\*\*\*/, 'keyword'],
      [/\*\*[^*\s].*?\*\*/, 'keyword'],
      [/\*[^*\s].*?\*/, 'keyword'],
      [/__[^_\s].*?__/, 'keyword'],
      [/_[^_\s].*?_/, 'keyword'],
      [/~~[^~\s].*?~~/, 'keyword'],
      [/!\[[^[\]\r\n]+\]\([^()\r\n]+\)/, 'tag'],
      [/\[[^[\]\r\n]+\]\([^()\r\n]+\)/, 'tag'],
      [/^(?:---|\*{3,}|_{3,})$/, 'comment'],
    ],
    codeblock: [
      [/[^`]+/, 'string'],
      [/^```$/, 'string', '@pop'],
      [/`/, 'string'],
    ],
  },
};

const csvLanguage: monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  tokenizer: {
    root: [{ include: '@field' }],
    field: [
      [/"/, 'string.value', '@quotedField'],
      [/[^,\n\r"]+/, 'text.content'],
      [/,/, 'delimiter.comma'],
    ],
    quotedField: [
      [/[^"]+/, 'string.value'],
      [/""/, 'string.escape'],
      [/"/, 'string.value', '@pop'],
    ],
  },
};

function registerLanguages(): void {
  monaco.languages.register({ id: 'json', extensions: ['.json'], aliases: ['JSON', 'json'] });
  monaco.languages.setMonarchTokensProvider('json', jsonLanguage);
  monaco.languages.setLanguageConfiguration('json', {
    brackets: [['{', '}'], ['[', ']']],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '"', close: '"' },
    ],
  });
  monaco.languages.register({ id: 'xml', extensions: ['.xml', '.xsd', '.xsl', '.svg'], aliases: ['XML', 'xml'] });
  monaco.languages.setMonarchTokensProvider('xml', xmlLanguage);
  monaco.languages.setLanguageConfiguration('xml', {
    comments: { blockComment: ['<!--', '-->'] },
    brackets: [['<', '>']],
    autoClosingPairs: [
      { open: '<', close: '>' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });
  monaco.languages.register({ id: 'markdown', extensions: ['.md', '.markdown'], aliases: ['Markdown', 'markdown'] });
  monaco.languages.setMonarchTokensProvider('markdown', markdownLanguage);
  monaco.languages.register({ id: 'csv', extensions: ['.csv'], aliases: ['CSV', 'csv'] });
  monaco.languages.setMonarchTokensProvider('csv', csvLanguage);
  // ── JSON folding: fold by { } and [ ] (works on any JSON layout) ──
  monaco.languages.registerFoldingRangeProvider('json', {
    provideFoldingRanges(model) {
      const ranges: monaco.languages.FoldingRange[] = [];
      const stack: number[] = [];
      const lineCount = model.getLineCount();
      for (let ln = 1; ln <= lineCount; ln++) {
        const text = model.getLineContent(ln);
        let inString = false;
        for (let i = 0; i < text.length; i++) {
          const c = text[i];
          if (inString) {
            if (c === '\\') i++;
            else if (c === '"') inString = false;
            continue;
          }
          if (c === '"') inString = true;
          else if (c === '{' || c === '[') stack.push(ln);
          else if (c === '}' || c === ']') {
            const start = stack.pop();
            if (start !== undefined && ln > start) ranges.push({ start, end: ln });
          }
        }
      }
      return ranges;
    },
  });
}

export function defineMonacoThemes(): void {
  monaco.editor.defineTheme('ediverse-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: '', foreground: 'D7DCE5' },
      { token: 'comment', foreground: '6B7A99', fontStyle: 'italic' },
      { token: 'white', foreground: 'D7DCE5' },
      // ---- JSON ----
      { token: 'string.key', foreground: '7DD3FC', fontStyle: 'bold' },
      { token: 'string.value', foreground: '5B8DEF' },
      { token: 'string.value.quote', foreground: '4A76C7' },
      { token: 'string.escape', foreground: 'B48EFF' },
      { token: 'number', foreground: 'B48EFF' },
      { token: 'number.float', foreground: 'B48EFF' },
      { token: 'number.hex', foreground: 'B48EFF' },
      { token: 'number.binary', foreground: 'B48EFF' },
      { token: 'keyword.boolean', foreground: 'C792EA' },
      { token: 'keyword.null', foreground: '8C97B8', fontStyle: 'italic' },
      { token: 'keyword', foreground: 'C792EA' },
      { token: 'delimiter.curly', foreground: 'E2E8F0' },
      { token: 'delimiter.square', foreground: 'E2E8F0' },
      { token: 'delimiter.comma', foreground: '6B7A99' },
      { token: 'delimiter.colon', foreground: 'A78BFA' },
      { token: 'delimiter', foreground: 'B9C2D0' },
      // ---- XML ----
      { token: 'tag', foreground: 'C792EA', fontStyle: 'bold' },
      { token: 'delimiter.angle', foreground: '5F7E97' },
      { token: 'attribute.name', foreground: '7DD3FC' },
      { token: 'attribute.value', foreground: '5B8DEF' },
      { token: 'delimiter.equals', foreground: 'A78BFA' },
      { token: 'text.content', foreground: 'A5D6FF' },
      { token: 'metatag', foreground: '8B9EF0', fontStyle: 'italic' },
      { token: 'constant.entity', foreground: 'B48EFF' },
      { token: 'string.cdata', foreground: '5B8DEF' },
      { token: 'string', foreground: '5B8DEF' },
    ],
    colors: {
      'editor.foreground': '#D7DCE5',
      'editor.background': '#272822',
      'editor.selectionBackground': '#49483E',
      'editor.lineHighlightBackground': '#3E3D32',
      'editorCursor.foreground': '#7DD3FC',
      'editorLineNumber.foreground': '#8C8C8C',
      'editorLineNumber.activeForeground': '#D7DCE5',
      'editorWhitespace.foreground': '#3B3A32',
      'editorWidget.background': '#1E1F1C',
      'editorWidget.border': '#3E3D32',
      'input.background': '#1E1F1C',
      'input.foreground': '#D7DCE5',
      'input.border': '#3E3D32',
      'editorGutter.foldingControlForeground': '#A5B4FC',
      'editor.foldBackground': '#6366F122',
    }
  });

  monaco.editor.defineTheme('ediverse-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: '', foreground: '1F2328' },
      { token: 'comment', foreground: '6E7781', fontStyle: 'italic' },
      { token: 'white', foreground: '1F2328' },
      // ---- JSON ----
      { token: 'string.key', foreground: '0969DA', fontStyle: 'bold' },
      { token: 'string.value', foreground: '24409A' },
      { token: 'string.value.quote', foreground: '5B7BC4' },
      { token: 'string.escape', foreground: '8250DF' },
      { token: 'number', foreground: 'BF3989' },                          // bright magenta
      { token: 'number.float', foreground: 'BF3989' },
      { token: 'number.hex', foreground: 'BF3989' },
      { token: 'number.binary', foreground: 'BF3989' },
      { token: 'keyword.boolean', foreground: '8250DF' },
      { token: 'keyword.null', foreground: '6E7781', fontStyle: 'italic' },
      { token: 'keyword', foreground: '8250DF' },
      { token: 'delimiter.curly', foreground: '1F2328' },
      { token: 'delimiter.square', foreground: '1F2328' },
      { token: 'delimiter.comma', foreground: '8C959F' },
      { token: 'delimiter.colon', foreground: 'A371F7' },                 // purple, was red
      { token: 'delimiter', foreground: '57606A' },
      // ---- XML ----
      { token: 'tag', foreground: '8250DF', fontStyle: 'bold' },
      { token: 'delimiter.angle', foreground: '8C959F' },
      { token: 'attribute.name', foreground: '0969DA' },
      { token: 'attribute.value', foreground: '24409A' },
      { token: 'delimiter.equals', foreground: 'A371F7' },
      { token: 'text.content', foreground: '0A7EA4' },                    // teal
      { token: 'metatag', foreground: '6639BA', fontStyle: 'italic' },
      { token: 'constant.entity', foreground: 'BF3989' },
      { token: 'string.cdata', foreground: '24409A' },
      { token: 'string', foreground: '24409A' },
    ],
    colors: {
      'editor.background': '#F8F9FA',
      'editor.foreground': '#1F2328',
      'editor.lineHighlightBackground': '#E8EAED',
      'editor.selectionBackground': '#BBCEE9',
      'editorCursor.foreground': '#0969DA',
      'editorLineNumber.foreground': '#8C959F',
      'editorLineNumber.activeForeground': '#0969DA',
      'editorBracketMatch.background': '#DDF4FF',
      'editorBracketMatch.border': '#54AEFF',
      'editorWidget.background': '#FFFFFF',
      'editorWidget.border': '#D0D7DE',
      'input.background': '#FFFFFF',
      'input.foreground': '#24292F',
      'input.border': '#D0D7DE',
      'scrollbar.shadow': '#1F232826',
      'scrollbarSlider.background': '#AFB8C166',
      'scrollbarSlider.hoverBackground': '#8C959FAA',
      'scrollbarSlider.activeBackground': '#6E7781CC',
      'editorGutter.foldingControlForeground': '#4F46E5',
      'editor.foldBackground': '#6366F114',
    }
  });
}